import { lazyLoadOnRender } from '@ms/yammer-libs-lazy';
import { reportUsage } from '@ms/yammer-telemetry';
import { combinePath } from '@ms/yammer-web-support/dist/location';
import type { ComponentType, FC, PropsWithChildren, ReactElement } from 'react';
import React, { memo, useEffect, useLayoutEffect } from 'react';
import { Redirect, Route, Switch, useLocation, useRouteMatch } from 'react-router-dom';

import { getHeroStringForTelemetry } from '../../telemetry/hero';
import { LoadableDogfoodPerfHUD } from '../DogfoodPerfHUD';
import PageDataLoadContextProvider from '../PageDataLoadContextProvider';
import { usePageTelemetryContextSetRouteCallback } from '../PageTelemetryContextProvider/hooks';

import type { AppPageRoutesWithTelemetryProps, PageRoute, ParentPageRoute } from './AppPageRoutesWithTelemetry.types';
import { parentRouteHasRedirectRoute } from './AppPageRoutesWithTelemetry.types';
import SubrouteComponentPreloader from './SubrouteComponentPreloader';
import { enforceNoDuplicatePageNames } from './enforceNoDuplicatePageNames';
import { isParentRoute } from './isParentRoute';
import { setRoutesForAssetPreloader } from './preloadPageAssets';

type ComponentClass = ComponentType | ComponentType<PropsWithChildren>;
const generatedLazyComponents = new Map<PageRoute['loader'], ComponentClass>();

const getCachedLazyComponent = (loader: PageRoute['loader'], fallback?: ReactElement): ComponentClass => {
  const cached = generatedLazyComponents.get(loader);
  if (cached) {
    return cached;
  }

  const newComponent = lazyLoadOnRender({ loader, fallback });
  generatedLazyComponents.set(loader, newComponent);

  return newComponent;
};

interface LoadablePageContentWithTelemetryProps {
  readonly route: PageRoute;
  readonly LoadablePageContent: ComponentClass;
}
const LoadablePageContentWithTelemetry: FC<LoadablePageContentWithTelemetryProps> = ({
  route,
  LoadablePageContent,
}) => {
  const { pathname } = useLocation();

  const setRoute = usePageTelemetryContextSetRouteCallback();

  useLayoutEffect(() => {
    setRoute?.(route, pathname);
  }, [pathname, route, setRoute]);

  useEffect(() => {
    const pageVisitEventName = route.page === 'Precache' ? 'page_visit_precache' : 'page_visit';
    reportUsage({
      eventName: pageVisitEventName,
      eventProperties: {
        page: route.page,
        hero: getHeroStringForTelemetry(route.hero),
        path: route.path,
        urlPath: pathname,
      },
    });
  }, [pathname, route.hero, route.page, route.path]);

  return route.dataLoader ? (
    <PageDataLoadContextProvider key={pathname} dataLoader={route.dataLoader}>
      <LoadablePageContent />
    </PageDataLoadContextProvider>
  ) : (
    <LoadablePageContent />
  );
};

type GetRouteElement = (route: PageRoute) => ReactElement;
const getRouteElement: GetRouteElement = (route) => {
  const LoadablePageContent = getCachedLazyComponent(route.loader);

  return (
    <Route
      key={route.path}
      path={route.path}
      exact={route.exact}
      render={() => <LoadablePageContentWithTelemetry route={route} LoadablePageContent={LoadablePageContent} />}
    />
  );
};

type GetFallbackRedirectElement = (to: string) => ReactElement;
const getFallbackRedirectElement: GetFallbackRedirectElement = (to) => (
  <Route key={to} path="*" render={() => <Redirect to={to} />} />
);

interface LoadableParentContentProps {
  readonly parentRoute: ParentPageRoute;
  readonly LoadableParentPageContent: ComponentClass;
}
const LoadableParentContent: FC<LoadableParentContentProps> = memo(({ parentRoute, LoadableParentPageContent }) => {
  const { pathname } = useLocation();
  const match = useRouteMatch({
    path: parentRoute.path,
  });
  const parentRoutePath = match ? match.url : pathname;
  const routeElements = parentRoute.childRoutes.map((route) =>
    getRouteElement({ ...route, path: combinePath(parentRoute.path, route.path) })
  );

  if (parentRouteHasRedirectRoute(parentRoute)) {
    routeElements.push(getRouteElement({ ...parentRoute.fallbackRedirectRoute, path: parentRoute.path }));
  } else {
    routeElements.push(getFallbackRedirectElement(parentRoute.fallbackRedirectPath));
  }

  return parentRoute.dataLoader ? (
    <PageDataLoadContextProvider key={parentRoutePath} dataLoader={parentRoute.dataLoader}>
      <LoadableParentPageContent>
        <Switch>{routeElements}</Switch>
      </LoadableParentPageContent>
    </PageDataLoadContextProvider>
  ) : (
    <LoadableParentPageContent>
      <Switch>{routeElements}</Switch>
    </LoadableParentPageContent>
  );
});

type GetParentRouteElement = (parentRoute: ParentPageRoute) => ReactElement;
const getParentRouteElement: GetParentRouteElement = (parentRoute) => {
  const LoadableParentPageContent = getCachedLazyComponent(
    parentRoute.loader,
    <SubrouteComponentPreloader parentRoute={parentRoute} />
  );

  return (
    <Route
      key={parentRoute.path}
      path={parentRoute.path}
      render={() => (
        <LoadableParentContent parentRoute={parentRoute} LoadableParentPageContent={LoadableParentPageContent} />
      )}
    />
  );
};

const AppPageRoutesWithTelemetry: FC<AppPageRoutesWithTelemetryProps> = ({ routes, disablePerfHUD }) => {
  enforceNoDuplicatePageNames(routes);
  useEffect(() => {
    setRoutesForAssetPreloader(routes);
  }, [routes]);

  const pageRoutes = routes.map((route) =>
    isParentRoute(route) ? getParentRouteElement(route) : getRouteElement(route)
  );

  const perfHUD = disablePerfHUD ? null : <LoadableDogfoodPerfHUD />;

  return (
    <>
      <Switch>{pageRoutes}</Switch>
      {perfHUD}
    </>
  );
};

export default memo(AppPageRoutesWithTelemetry);
